import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { AppHeader } from "@/components/AppHeader";
import { renderMarkdown } from "@/lib/renderMarkdown";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Sparkles,
  RotateCcw,
  Loader2,
  Trophy,
} from "lucide-react";

export default function QuizPage() {
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  const loadQuiz = useCallback(() => {
    setLoading(true);
    setError(null);
    setCurrent(0);
    setSelected(null);
    setRevealed(false);
    setAnswers([]);
    setFinished(false);
    api
      .post("/quiz/generate", { topic_id: topicId, num_questions: 10 })
      .then((r) => setQuiz(r.data))
      .catch((e) => {
        if (e.isSubscriptionError) {
          setError("Your free trial has ended. Subscribe to take quizzes.");
        } else {
          setError(e?.response?.data?.detail || "Failed to generate quiz");
          toast.error("Couldn't generate quiz");
        }
      })
      .finally(() => setLoading(false));
  }, [topicId]);

  useEffect(() => {
    loadQuiz();
  }, [loadQuiz]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[hsl(var(--brimi-coral))]/15 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[hsl(var(--brimi-coral))] animate-spin" />
          </div>
          <h2 className="text-2xl font-black mb-2">Crafting your quiz…</h2>
          <p className="text-muted-foreground">
            Brimi is preparing questions tailored to your chapter.
          </p>
        </main>
      </div>
    );
  }

  if (error || !quiz?.questions?.length) {
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-6 py-16 text-center">
          <h2 className="text-2xl font-black mb-3">Couldn't load quiz</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button
            data-testid="quiz-retry-btn"
            onClick={loadQuiz}
            className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-6 py-3"
          >
            <RotateCcw className="w-4 h-4" /> Try again
          </button>
        </main>
      </div>
    );
  }

  const total = quiz.questions.length;
  const q = quiz.questions[current];

  const correctCount = answers.filter((a) => a.correct).length;

  const choose = (i) => {
    if (revealed) return;
    setSelected(i);
    setRevealed(true);
    setAnswers((a) => [
      ...a,
      { index: current, selected: i, correct: i === q.correct_index },
    ]);
  };

  const next = () => {
    if (current + 1 >= total) {
      setFinished(true);
      // Persist attempt (fire-and-forget — we already have the score locally)
      const correctNow = answers.filter((a) => a.correct).length;
      api
        .post("/quiz/attempts", {
          quiz_id: quiz.id,
          topic_id: quiz.topic_id,
          total_questions: total,
          correct_count: correctNow,
        })
        .catch(() => {
          /* swallow — progress save is best-effort, no need to spam toasts */
        });
      return;
    }
    setCurrent((c) => c + 1);
    setSelected(null);
    setRevealed(false);
  };

  if (finished) {
    const pct = Math.round((correctCount / total) * 100);
    return (
      <div className="min-h-screen">
        <AppHeader />
        <main className="max-w-2xl mx-auto px-6 py-12">
          <div className="tactile-card p-8 sm:p-10 text-center" data-testid="quiz-result">
            <div className="w-20 h-20 mx-auto mb-6 rounded-3xl bg-[hsl(var(--brimi-yellow))]/25 flex items-center justify-center">
              <Trophy className="w-10 h-10 text-[hsl(var(--brimi-coral))]" />
            </div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">
              Quiz complete
            </div>
            <h1 className="text-5xl font-black mb-1">
              {correctCount}
              <span className="text-2xl text-muted-foreground">/{total}</span>
            </h1>
            <div className="text-2xl font-bold mb-2 text-[hsl(var(--brimi-blue))]">
              {pct}%
            </div>
            <p className="text-muted-foreground mb-8">
              {pct >= 80
                ? "Brilliant work! 🌟"
                : pct >= 50
                  ? "Good effort — keep practicing! 💪"
                  : "Let's review this chapter and try again. 📚"}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                data-testid="quiz-restart-btn"
                onClick={loadQuiz}
                className="pill-btn bg-[hsl(var(--brimi-blue))] text-white px-6 py-3"
              >
                <RotateCcw className="w-4 h-4" /> New quiz
              </button>
              <button
                data-testid="quiz-back-btn"
                onClick={() => navigate(-1)}
                className="pill-btn bg-foreground/5 hover:bg-foreground/10 px-6 py-3"
              >
                Back to chapters
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-2xl mx-auto px-6 py-12">
        <button
          onClick={() => navigate(-1)}
          data-testid="quiz-exit-btn"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Exit quiz
        </button>

        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">
            {quiz.subject} · Class {quiz.class_id}
          </div>
          <div className="text-sm font-bold">
            <span className="text-[hsl(var(--brimi-blue))]">{current + 1}</span>
            <span className="text-muted-foreground"> / {total}</span>
          </div>
        </div>

        <div className="h-2 w-full bg-muted rounded-full overflow-hidden mb-8">
          <div
            className="h-full bg-[hsl(var(--brimi-blue))] transition-all"
            style={{ width: `${((current + (revealed ? 1 : 0)) / total) * 100}%` }}
          />
        </div>

        <div className="tactile-card p-6 sm:p-8" data-testid="quiz-question-card">
          <div className="text-sm font-bold text-[hsl(var(--brimi-coral))] uppercase tracking-widest mb-3">
            {quiz.topic_title}
          </div>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight mb-7">
            {renderMarkdown(q.question)}
          </h2>

          <div className="space-y-3">
            {q.options.map((opt, i) => {
              const isCorrect = revealed && i === q.correct_index;
              const isWrong = revealed && i === selected && i !== q.correct_index;
              const base =
                "w-full text-left flex items-center gap-3 px-4 py-3 rounded-2xl border-2 transition-all font-medium";
              const cls = isCorrect
                ? "bg-[hsl(var(--brimi-mint))]/15 border-[hsl(var(--brimi-mint))] text-foreground"
                : isWrong
                  ? "bg-destructive/10 border-destructive text-foreground"
                  : selected === i
                    ? "bg-[hsl(var(--brimi-blue))]/10 border-[hsl(var(--brimi-blue))]"
                    : "bg-card border-border hover:border-[hsl(var(--brimi-blue))]/40 hover:bg-muted";
              return (
                <button
                  key={i}
                  data-testid={`quiz-option-${i}`}
                  onClick={() => choose(i)}
                  disabled={revealed}
                  className={`${base} ${cls}`}
                >
                  <span className="w-7 h-7 rounded-lg bg-foreground/5 flex items-center justify-center text-sm font-black shrink-0">
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1">{renderMarkdown(opt)}</span>
                  {isCorrect && (
                    <CheckCircle2 className="w-5 h-5 text-[hsl(var(--brimi-mint))] shrink-0" />
                  )}
                  {isWrong && (
                    <XCircle className="w-5 h-5 text-destructive shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          {revealed && (
            <div
              className="mt-6 p-4 rounded-2xl bg-muted text-sm leading-relaxed border border-border"
              data-testid="quiz-explanation"
            >
              <div className="font-bold mb-1 flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[hsl(var(--brimi-coral))]" />
                Explanation
              </div>
              {renderMarkdown(q.explanation)}
            </div>
          )}

          {revealed && (
            <button
              data-testid="quiz-next-btn"
              onClick={next}
              className="mt-6 w-full pill-btn bg-[hsl(var(--brimi-blue))] hover:bg-[hsl(var(--brimi-blue))]/90 text-white py-4 text-base"
            >
              {current + 1 >= total ? "Finish quiz" : "Next question"}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </main>
    </div>
  );
}
